import {
    BoxedRow,
    Callout,
    Carousel,
    HorizontalScroll,
    IconAlertRegular,
    IconInformationUserRegular,
    IconWarningRegular,
    skinVars,
    useDialog,
    useWindowSize,
} from '@telefonica/mistica';
import { formatMobileNumber } from '../../utils/dataTransformation/unitsConversion';
import './index.scss';
import { InfoSIM } from '../../models/ConfigureLine';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useConfigureLineContext } from '../../context/configure-line-context/ConfigureLineContext';
import { ChangeOwnershipStatus } from '../../models/OwnershipChange';
import { useChangeOwnershipContext } from '../../context/change-ownership-context/ChangeOwnershipContext';
import { managementEnvironment } from '../../utils/dataTransformation/environments';
import { usePersonalDataContext } from '../../context/personal-data-context/PersonalDataContext';
import useChangeOwnership from '../../hooks/useChangeOwnership';

type WarningLineType = {
    warningsInfo?: InfoSIM[];
    blockedLine?: {
        blocked: boolean;
        number: string;
        productId: string;
    };
    changeOfOwnership?: ChangeOwnershipStatus | null;
};

export const WarningLine = ({ warningsInfo, blockedLine, changeOfOwnership }: WarningLineType) => {
    const { dialog } = useDialog();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(true);
    const [warningList, setWarningList] = useState<InfoSIM[]>([]);
    const size = useWindowSize();
    const isConvergent: boolean = true;
    const { setLineActive } = useConfigureLineContext();
    const { setFlowStartUrl, dataChangeOwnership } = useChangeOwnershipContext();
    const ownershipChangeEnabled = managementEnvironment().VITE_WEBVIEW_OWNERSHIP_CHANGE_ENABLED;
    const { personalData } = usePersonalDataContext();
    const { initializeChangeOwnership } = useChangeOwnership();
    const isDonante =
        !dataChangeOwnership?.interested?.interestedId ||
        (dataChangeOwnership?.interested?.interestedId &&
            personalData?.id !== dataChangeOwnership?.interested?.interestedId);

    useEffect(() => {
        const filteredWarnings =
            warningsInfo?.filter(
                (simInfo) => simInfo.pending || simInfo.multiSims?.some((multiSim) => multiSim.pending),
            ) ?? [];

        setWarningList(filteredWarnings);
    }, [warningsInfo]);

    const getWarningTitle = (simInfo: InfoSIM) => {
        if (simInfo.multiSims && simInfo.multiSims.some((multiSim) => multiSim.pending))
            return 'multiSimActivationWarning';
        else {
            if (simInfo.type === 'E_SIM') return 'eSimInstallWarning';
            else return 'simActiveWarning';
        }
    };

    const handleChangeOfOwnerhip = () => {
        if (personalData.fiscalIdType.includes('CIF')) {
            dialog({
                asset: <IconInformationUserRegular color={skinVars.colors.backgroundBrandSecondary} size={40} />,
                title: t('changeHolderCifRestrictedTitle'),
                acceptText: t('buttonUnderstoodFeedback'),
                message: t('cifChangeÖwnershipDialog'),
                onAccept() {},
            });
        } else {
            setFlowStartUrl('/home');
            initializeChangeOwnership();
        }
    };

    if (!Array.isArray(warningsInfo)) {
        return (
            <div className='warningEsim-component'>
                {isVisible && (
                    <div className='warningEsim-component__callout'>
                        <Callout
                            title='Ha fallado en la carga de información.'
                            description='Lo sentimos, en este momento no podemos mostrarte esta información de recordatorio. Vuelve a intentarlo más tarde.'
                            asset={
                                <IconWarningRegular color={skinVars.colors.error} size={size.width >= 1024 ? 24 : 16} />
                            }
                            onClose={() => {
                                setIsVisible(false);
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    const totalWarnings =
        warningList.length +
        (blockedLine?.blocked ? 1 : 0) +
        (ownershipChangeEnabled && changeOfOwnership && changeOfOwnership.status === 'CONTINUE' && !isDonante ? 1 : 0);

    return (
        <div className='warningEsim-component'>
            <div className='warningEsim-component__convergent'>
                {size.width < 1024 ? (
                    <HorizontalScroll>
                        {warningsInfo?.map((simInfo, index) => {
                            if (simInfo.pending || simInfo.multiSims?.some((multiSim) => multiSim.pending))
                                return (
                                    <div className='warningEsim-component__convergent-boxedRow' key={index}>
                                        <BoxedRow
                                            onPress={() => {
                                                setLineActive(simInfo.productId);
                                                navigate(`/configure-line`, {
                                                    state: { isConvergent },
                                                });
                                            }}
                                            role='warning'
                                            touchableRole='warning'
                                            title={t(getWarningTitle(simInfo))}
                                            description={formatMobileNumber(simInfo?.number)}
                                            asset={<IconAlertRegular size={24} color='currentColor' />}
                                            danger={true}
                                            withChevron={true}
                                        />
                                    </div>
                                );
                        })}
                        {blockedLine?.blocked && (
                            <div className='warningEsim-component__convergent-boxedRow'>
                                <BoxedRow
                                    onPress={() => {
                                        setLineActive(blockedLine.productId);
                                        navigate(`/configure-line`, {
                                            state: { isConvergent },
                                        });
                                    }}
                                    role='warning'
                                    touchableRole='warning'
                                    title={t('blockedLine')}
                                    description={formatMobileNumber(blockedLine.number)}
                                    asset={<IconAlertRegular size={24} color='currentColor' />}
                                    danger={true}
                                    withChevron={true}
                                />
                            </div>
                        )}
                        {ownershipChangeEnabled &&
                            changeOfOwnership &&
                            changeOfOwnership.status === 'CONTINUE' &&
                            !isDonante && (
                                <div className='warningEsim-component__convergent-boxedRow'>
                                    <BoxedRow
                                        role='warning'
                                        touchableRole='warning'
                                        title={t('warningSignature')}
                                        description={t('ChangeOfOwnership')}
                                        asset={<IconAlertRegular size={24} color='currentColor' />}
                                        withChevron={true}
                                        danger={true}
                                        onPress={handleChangeOfOwnerhip}
                                    />
                                </div>
                            )}
                    </HorizontalScroll>
                ) : (
                    <div className='warningEsim-component__convergent-wrapper'>
                        <Carousel
                            initialActiveItem={0}
                            withBullets={totalWarnings > 3}
                            withControls={totalWarnings > 3}
                            itemsPerPage={totalWarnings > 2 ? 3 : totalWarnings}
                            itemStyle={{ width: totalWarnings <= 3 ? 'fit-content' : '' }}
                            items={[
                                ...warningList.map((simInfo, index) => {
                                    if (simInfo.pending || simInfo.multiSims.some((multiSim) => multiSim.pending))
                                        return (
                                            <div className='warningEsim-component__convergent-boxedRow' key={index}>
                                                <BoxedRow
                                                    onPress={() => {
                                                        setLineActive(simInfo.productId);
                                                        navigate(`/configure-line`, {
                                                            state: { isConvergent },
                                                        });
                                                    }}
                                                    role='warning'
                                                    touchableRole='warning'
                                                    title={t(getWarningTitle(simInfo))}
                                                    description={formatMobileNumber(simInfo?.number)}
                                                    asset={<IconAlertRegular size={24} color='currentColor' />}
                                                    withChevron={true}
                                                    danger={true}
                                                />
                                            </div>
                                        );
                                }),
                                blockedLine?.blocked && (
                                    <div className='warningEsim-component__convergent-boxedRow'>
                                        <BoxedRow
                                            onPress={() => {
                                                setLineActive(blockedLine?.productId);
                                                navigate(`/configure-line`, {
                                                    state: { isConvergent },
                                                });
                                            }}
                                            role='warning'
                                            touchableRole='warning'
                                            title={t('blockedLine')}
                                            description={formatMobileNumber(blockedLine?.number)}
                                            asset={<IconAlertRegular size={24} color='currentColor' />}
                                            withChevron={true}
                                            danger={true}
                                        />
                                    </div>
                                ),
                                ownershipChangeEnabled &&
                                    changeOfOwnership &&
                                    changeOfOwnership.status === 'CONTINUE' &&
                                    !isDonante && (
                                        <div className='warningEsim-component__convergent-boxedRow'>
                                            <BoxedRow
                                                role='warning'
                                                touchableRole='warning'
                                                title={t('warningSignature')}
                                                description={t('ChangeOfOwnership')}
                                                asset={<IconAlertRegular size={24} color='currentColor' />}
                                                withChevron={true}
                                                danger={true}
                                                onPress={handleChangeOfOwnerhip}
                                            />
                                        </div>
                                    ),
                            ]}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
